import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { EMPTY, Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { PASS_ERROR_HEADER, SKIP_TOKEN } from '../consts/api.const';

@Injectable()
export class EntraAuthInterceptor implements HttpInterceptor {
  private readonly scopes = environment.msalScopeUri
    ? [environment.msalScopeUri]
    : [];

  constructor(private msalService: MsalService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const shouldThrowOutside = req.headers.has(PASS_ERROR_HEADER);

    if (req.headers.has(SKIP_TOKEN)) {
      return next.handle(req.clone({ headers: req.headers.delete(SKIP_TOKEN) }));
    }

    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      return next
        .handle(req)
        .pipe(catchError((err: unknown) => (shouldThrowOutside ? throwError(() => err) : EMPTY)));
    }

    return from(
      this.msalService.instance.acquireTokenSilent({ scopes: this.scopes, account }),
    ).pipe(
      switchMap((result: AuthenticationResult) => {
        const authReq = req.clone({
          setHeaders: { Authorization: `Bearer ${result.accessToken}` },
        });
        return next.handle(authReq);
      }),
      catchError((error: unknown) => {
        if (error instanceof InteractionRequiredAuthError) {
          // Token expired or missing consent – trigger interactive redirect
          this.msalService.instance.acquireTokenRedirect({ scopes: this.scopes, account });
          return EMPTY;
        }
        return shouldThrowOutside ? throwError(() => error) : EMPTY;
      }),
    );
  }
}
